import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ShieldAlert, BarChart2, ChevronLeft, ChevronRight, UserCog, FileCode2 } from 'lucide-react';
import { RulesTab } from './components/RulesTab';
import { AlertsTab } from './components/AlertsTab';
import { OnCallTab } from './components/OnCallTab';
import { TemplatesTab } from './components/TemplatesTab';
import { InsightsTab } from './components/InsightsTab';

export const AutomationMain: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'rules';
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  const setTab = (tabName: string) => {
    setSearchParams({ tab: tabName });
  };

  const navItems = [
    { id: 'rules', label: 'Workflows & Rules', icon: UserCog },
    { id: 'alerts', label: 'Alert Log', icon: ShieldAlert },
    { id: 'templates', label: 'Templates', icon: FileCode2 },
    // { id: 'oncall', label: 'On-Call Roster', icon: Calendar },
    { id: 'insights', label: 'Insights & Health', icon: BarChart2 },
  ];

  return (
    <div className="h-screen bg-gray-900 text-gray-100 font-sans overflow-hidden flex flex-col pb-20">
      {/* Top Header */}
      <div className="flex-none flex items-center justify-between px-4 pt-2.5 pb-1.5 border-b border-gray-800">
        <h1 className="text-base font-bold text-white flex items-center gap-2 tracking-tight text-lg">
          <div className="p-1.5 rounded bg-orange-600">
            <UserCog className="w-5 h-5 text-white" />
          </div>
          Automation Center
        </h1>
        <div className="text-[10px] text-gray-200 font-semibold uppercase bg-gray-850 px-2 py-0.5 rounded border border-gray-800">
          EVENT ENGINE v1.2.0
        </div>
      </div>

      {/* Main Workspace Layout */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar Nav */}
        <div className={`bg-gray-950/40 border-r border-gray-800 flex flex-col p-2 space-y-1 transition-all duration-300 ease-in-out shrink-0 relative z-20 ${isSidebarCollapsed ? 'w-14' : 'w-52'}`}>
          {/* Header & Toggle */}
          <div className="flex items-center justify-between px-1 mb-1">
            {!isSidebarCollapsed && (
              <span className="text-[9px] text-gray-400 uppercase tracking-widest font-bold block px-1 truncate">
                Navigation
              </span>
            )}
            <button
              type="button"
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className={`p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors cursor-pointer ${isSidebarCollapsed ? 'mx-auto' : 'ml-auto'}`}
              title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
            >
              {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>

          {navItems.map((item) => {
            const Icon = item.icon;
            const active = currentTab === item.id;
            return (
              <div key={item.id} className="relative group">
                <button
                  type="button"
                  onClick={() => setTab(item.id)}
                  className={`w-full flex items-center ${isSidebarCollapsed ? 'justify-center px-2 py-2' : 'gap-2.5 px-2.5 py-1.5'} rounded-lg text-xs font-semibold tracking-wide transition-all duration-150 cursor-pointer ${active
                    ? 'bg-orange-700 text-white shadow-md shadow-orange-500/10'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                    }`}
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  {!isSidebarCollapsed && <span className="truncate">{item.label}</span>}
                </button>

                {/* Orange Badge Tooltip when collapsed */}
                {isSidebarCollapsed && (
                  <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center px-3 py-1.5 bg-orange-600 text-white text-[10px] font-extrabold uppercase tracking-wider rounded-lg shadow-2xl shadow-orange-600/30 whitespace-nowrap z-50 pointer-events-none">
                    {/* Left Pointer Notch */}
                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-orange-600 rotate-45"></div>
                    <span className="relative z-10">{item.label}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Tab View Content Pane */}
        <div className="flex-1 min-w-0 p-3 overflow-hidden">
          {currentTab === 'rules' && <RulesTab />}
          {currentTab === 'alerts' && <AlertsTab />}
          {currentTab === 'oncall' && <OnCallTab />}
          {currentTab === 'templates' && <TemplatesTab />}
          {currentTab === 'insights' && <InsightsTab />}
        </div>
      </div>
    </div>
  );
};

export default AutomationMain;
